import { TamediaStyles } from "@ta-interaktiv/microfrontend-template-helpers";
import styled, { createGlobalStyle } from 'styled-components'
import { useState, useEffect } from 'react'
import { TextWidth } from '@ta-interaktiv/react-disco-grid'
import { GlobalVariables } from 'Globals/GlobalVariables'
import mapboxgl from 'mapbox-gl'
import {
  MapboxMap,
  AttributionControl,
  ScaleControl,
  GeolocateControl,
  NavigationControls,
  ActivationButton,
  PitchControl,
} from 'components/MapboxMap'
import { HazardLayerSwitch } from 'components/HazardLayerSwitch'
import { GetPos } from 'components/getPos'
import { SectionHead } from 'components/SectionHead'
import { Scrollchor } from 'react-scrollchor'
import { Link } from '@ta-interaktiv/contentbricks'

const MainComponent = ({
  tenant,
  colorMode,
  properties,
  trackEvent,
}: MicroFrontendProps) => {
  const GlobalStyles = createGlobalStyle`
#app {
  // prevents "swimming" layout on mobile
  overflow: hidden;

  .map-img {
    img {
      width: 100%;
      margin-top: 2em;
    }
  }

  .mapboxgl-ctrl-scale {
    display: none;
  }

  span.tooltip {
    position: absolute;
    max-width: 200px;
    padding: 0.75em 1em;
    font-size: 16px;
    line-height: 1.2em;
    text-align: center;
    color: #6f7187;
    background: #fff;
    -webkit-border-radius: 0;
    border-radius: 0;
    box-shadow: 2px 2px 7px rgba(0, 0, 0, 0.2);

    &:after {
      content: '';
      position: absolute;
      width: 0;
      height: 0;
      border-width: 10px;
      border-style: solid;
      left: -webkit-calc(50% - 10px);
      left: calc(50% - 10px);
    }

    &.activation {
      top: 95px;
      right: 50px;
    }

    &.layers {
      bottom: 85px;
      left: calc(50% - 100px);
    }

    &.bottom:after {
      bottom: -26px;
      border-color: #6f7187 transparent transparent;
      animation: hint-bottom 2s infinite;
    }

    @keyframes hint-bottom {
      0% {
        transform: translateY(0px);
      }

      100% {
        transform: translateY(6px);
      }
    }

    @keyframes hint-top {
      0% {
        transform: translateY(0px);
      }

      100% {
        transform: translateY(-6px);
      }
    }

    &.top:after {
      top: -26px;
      border-color: transparent transparent #6f7187;
      left: -webkit-calc(75% - 10px);
      left: calc(75% - 10px);
      animation: hint-top 2s infinite;
    }
  }

  div[class^=" css-"][class$="control"] {
    position: relative;
  }

  div[class^=" css-"][class$="placeholder"] {
    position: absolute;
    top: 0;
    left: 0;
  }

  div[class^=" css-"][class$="Text"] {
    display: none;
  }

  div[class^=" css-"][class$="menu"] {
    z-index: 1000;
    position: absolute;
    background: #fff;

    div {
      border-bottom: 1px solid #eee;
      cursor: pointer;
      padding: 0.5em 1em;
    }
  }

  a.cB:hover,
  a.cB:link,
  a.cB:active,
  a.cB:visited {
    text-decoration: none;
    color: var(--text-color);
  }

  a {
    color: #c51b8a;
  }

  .button.primary {
    background-color: #c51b8a;
  }

  .scrollchor {
    a {
      font-family: tamediaUI;
      color: #c51b8a;
      font-weight: 400;
      background-color: #c51b8a15;
    }
  }

  .grey-special.segment {
    background-color: #f5f6f7;
  }

  .map-parameters {
    font-size: 0.75em;
    font-family: var(--font-sans-serif);

    li {
      line-height: 1.5em;
    }
  }

  .map-section {
    font-family: tamediaUI;

    h4 {
      font-family: tamediaUI;
      font-weight: 800;
      text-align: center;
    }
  }

  .ta-small {
    font-size: 0.75rem;
    opacity: 0.75;
    font-family: tamediaUI;
    color: var(--text-color);
    margin-top: 1em;
  }

  .map-legend {
    color: var(--text-color);
    flex-direction: row;
    font-family: var(--font-sans-serif);

    .legend-left {
      display: inline-block;
      width: 100%;
    }

    .legend-right {
      display: none;
      display: inline-block;
      width: 25%;

      font-family: TamediaChart;
      color: #999999;
      font-size: 0.85rem;
    }

    .legend-icon {
      display: inline-block;
      width: 60px;
      margin-right: 10px;

      img {
        width: 100%;
        height: auto;
      }
    }

    .legend-bars-labels {
      font-family: var(--font-sans-serif);
      font-size: 0.75em;
      color: #999;
      width: 100%;
      display: block;

      .left,
      .right {
        width: 50%;
        display: inline-block;
      }
      .right {
        text-align: right;
      }
    }

    .legend-bars-wrapper {
      display: block;
      width: 100%;
      display: inline-block;
    }

    .legend-bars-container {
      .legend-box {
        display: inline-block;
        width: calc(20% - 2px);
        font-family: TamediaChart;
        font-size: 0.75rem;
        color: #999999;
        margin-right: 2px;

        &:last-child {
          text-align: right;
        }

        .legend-bar {
          margin-top: 0.25rem;
          height: 12px;
          width: 100%;
          background-color: #f5f6f7;
        }
      }

      &.windatlas,
      &.visibility {
        .legend-box {
          &:nth-child(1) {
            .legend-bar {
              background-color: #7a0177;
            }
          }
          &:nth-child(2) {
            .legend-bar {
              background-color: #c51b8a;
            }
          }
          &:nth-child(3) {
            .legend-bar {
              background-color: #f768a1;
            }
          }
          &:nth-child(4) {
            .legend-bar {
              background-color: #fbb4b9;
            }
          }
          &:nth-child(5) {
            .legend-bar {
              background-color: #feebe2;
            }
          }
        }
      }
    }

    .legend-title {
      font-weight: 200;
      b {
        font-weight: 900;
      }
    }
  }

  .legend-bloc {
    margin-top: 2rem;
    margin-bottom: 2rem;
    clear: both;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .icon-wrapper {
    width: 70px;
    height: 70px;
    float: left;
    margin-right: 1rem;
    margin-bottom: 1rem;
    margin-top: 0.5rem;

    img {
      width: 100%;
    }
  }

  .ta.hint {
    font-weight: 200;
    font-size: 0.9em;
    background: var(--footer-footertop-background);
    font-family: var(--chart-font-stack);
    color: var(--text-color);
    margin-top: 2rem;
    padding: 1em;
    line-height: 1.4;
  }

  .MapboxMap,
  .MapboxMap #map  {
    font-family: var(--chart-font-stack);
    clear: both;
    position: relative;
    height: 90vh !important;
    min-height: 650px;
  }
}

`
  const [latlng, setLatlng] = useState([0, 0])
  const [map, registerMap] = useState<any>(undefined)
  const isMobile = window.innerWidth < 768
  const layers = [
    { name: 'Pour les frileux', id: 'cat-frileux', color: '#c51b8a' },
    { name: 'Pour les puristes', id: 'cat-puristes', color: '#c51b8a' },
    { name: 'En train', id: 'cat-ecolos', color: '#c51b8a' },
    { name: 'Tout le territoire', id: 'cat-base', color: '#c51b8a' },
  ]

  useEffect(() => {
    if (map) {
      map.flyTo({
        center: latlng,
        zoom: 10,
      })
    }
  }, [latlng])

  return (
    <AppContainer $colorMode={colorMode}>
      <GlobalStyles />
      {map && (
        <div className='ui very fitted vertical segment'>
          <TextWidth>
            <SectionHead>Légende</SectionHead>
            <div className='map-legend'>
              <div className='legend-left'>
                <div className='legend-title'>
                  <b>Pollution lumineuse:</b> radiance (10<sup>10</sup>)
                </div>
                <div className='legend-bars-container visibility'>
                  <div className='legend-bars-labels'>
                    <div className='left'>Faible: 0</div>
                    <div className='right'>Élevée: 500</div>
                  </div>
                  <div className='legend-bars-wrapper'>
                    <div className='legend-box'>
                      <div className='legend-bar' />
                    </div>
                    <div className='legend-box'>
                      <div className='legend-bar' />
                    </div>
                    <div className='legend-box'>
                      <div className='legend-bar' />
                    </div>
                    <div className='legend-box'>
                      <div className='legend-bar' />
                    </div>
                    <div className='legend-box'>
                      <div className='legend-bar' />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className='ta hint big'>
              <p>
                Nous avons pris en compte la lumière reçue par les satellites (
                <b>radiance</b>). Plus celle-ci est faible, moins le ciel est
                affecté par la pollution lumineuse. Toutefois, la seule mesure
                de la radiance ne donne qu'un aperçu du réel impact de
                l'éclairage artificiel.
              </p>
              {/* <p>
                La <b>pollution lumineuse</b> se manifeste de manière directe ou
                diffuse. Ainsi, la réflexion de la lumière sur les surfaces, les
                aérosols ou les molécules en suspension dans l’atmosphère
                produit un halo lumineux qui voile le ciel nocturne.
              </p> */}
              <p>
                <b>
                  À titre d'exemple, au coeur de Lausanne ou Genève, la radiance
                  est maximale (500).
                </b>
              </p>
            </div>
          </TextWidth>
        </div>
      )}

      {/* {map && (
        <TextWidth>
          <div className='ui container'>
            <SectionHead>Trouver une adresse ou un lieu</SectionHead>
          </div>
          <div className='ui text container'>
            <GetPos
              handleResult={(res) => {
                setLatlng(res)
              }}
            />
          </div>
        </TextWidth>
      )} */}
      <MapContainer id='map'>
        <MapboxMap
          getMap={(map: mapboxgl.Map) => registerMap(map)}
          token='pk.eyJ1IjoidGEtaW50ZXJha3RpdiIsImEiOiJjaXNhNGFsdHAwMDB2Mm9wNWdlMTlqMTNuIn0.uj5aGvF3yvEM4V6kNdlbVg'
          initOptions={{
            style: 'mapbox://styles/ta-interaktiv/cks3jhvua7ykb18qggwd42s81',
            zoom: isMobile ? 6.75 : 7,
            maxZoom: 17,
            minZoom: isMobile ? 3 : 7,
            center: isMobile ? [7, 46.6] : [8.23, 46.6],
            attributionControl: false,
          }}
        >
          <>
            <AttributionControl
              options={{ customAttribution: 'Source des géodonnées' }}
            />
            <ScaleControl position='bottom-left' />
            <NavigationControls />
            <PitchControl />
            <GeolocateControl />
            <ActivationButton
              locales={{
                activate: 'Naviguer dans la carte',
                deactivate: 'Quitter la navigation',
              }}
            />
            {layers && (
              <HazardLayerSwitch
                isMobile={false}
                hazards={layers.map((m) => {
                  return m
                })}
              />
            )}
          </>
        </MapboxMap>
      </MapContainer>
      <div className='ui vertical segment map-continue'>
        <TextWidth>
          <div>
            <h4 className='title'>En savoir plus sur la carte</h4>
            <p>
              Pour réaliser cette carte, nous avons compilé les données
              disponibles les plus récentes des émissions lumineuses de l'Office
              fédéral de l'environnement et du modèle numérique du terrain de
              l'Office fédéral de la topographie.
            </p>
            <p>
              Nous avons croisé trois paramètres (les émissions lumineuses,
              l'altitude et la visibilité du ciel) afin de déterminer les
              endroits les plus propices à l'observation du ciel nocturne pour
              chacun des trois scénarios suivants:
            </p>
            <div className='legend-bloc'>
              <div className='icon-wrapper'>
                <img src={require('../../images/icons_parcs_colored.svg').default} />
              </div>
              <p>
                <b>Pour les frileux</b> Si vous voulez observer le ciel sans
                vous exposer trop au froid. Nous avons trouvé les zones situées
                à moins de 500m d'altitude, offrant un minimum de 90% de
                visibilité du ciel et une pollution lumineuse faible à modérée.
                En contrepartie, vous devrez sacrifier un peu de visibilité.{' '}
                {/* <span className='scrollchor'>
                  <Scrollchor to='map'>Vers la carte →</Scrollchor>
                </span> */}
              </p>
            </div>

            <div className='legend-bloc'>
              <div className='icon-wrapper'>
                <img
                  src={require('../../images/icons_windatlas_colored.svg').default}
                />
              </div>
              <p>
                <b>Pour les puristes</b> Si vous n'êtes prêts à aucune
                concession dans votre quête du ciel parfait, nous avons compilé
                les endroits les moins affectés par la pollution lumineuse avec
                une ouverture maximale sur la voûte céleste.
              </p>
              <p>
                Nous ne tenons pas compte de l'altitude dans nos paramètres, ni
                de la proximité de routes ou transports en commun. Alors
                préparez vos chaussures de marche.{' '}
                {/* <span className='scrollchor'>
                  <Scrollchor to='map'>Vers la carte →</Scrollchor>
                </span> */}
              </p>
            </div>

            <div className='legend-bloc'>
              <div className='icon-wrapper'>
                <img
                  src={require('../../images/icons_visibility_colored.svg').default}
                />
              </div>
              <p>
                <b>En train</b> Pour favoriser les transports en commun, nous
                avons sélectionné les lieux éloignés de moins de 2km d'une gare
                suisse. Ils devraient vous permettre d'observer un ciel sombre
                et dégagé, pour autant que vous acceptiez de marcher un peu une
                fois arrivés à destination.
              </p>
              <p>
                Attention au dernier train. Ces renseignements sont purement
                indicatifs, renseignez-vous avant de partir.{' '}
                {/* <span className='scrollchor'>
                  <Scrollchor to='map'>Vers la carte →</Scrollchor>
                </span> */}
              </p>
            </div>
          </div>
        </TextWidth>
      </div>

      <div className='ui vertical very fitted segment'>
        <TextWidth>
          <Text>
            <h4 className='title'>Selon les calculs</h4>
            <p>
              D'après notre analyse, les meilleurs spots d’observation se
              trouvent évidemment en altitude: dans les Alpes, mais aussi dans
              le Jura, par exemple près du col du Marchairuz, sous la cabane du
              Grand Cunay ou à la Roche Champion, tout proche de la frontière
              française.
            </p>
            <p>
              Si l’on ne souhaite pas s’aventurer trop haut, le Nord vaudois
              permet de s’isoler un peu des sources lumineuses, notamment entre
              Onnens et Vaumarcus, ou entre Cheseaux-Noréaz et Yvonand.
            </p>
            <p>
              Pour ceux qui prévoient une expédition avec un retour en train,
              notre carte propose de poser sa couverture près de Ballens, sur la
              ligne du MBC, ou entre St-Cergue et La Cure, avec le NSC.
            </p>
            <p>
              Dans le canton de Genève, les conditions de luminosité ne sont pas
              idéales, mais elles seront un peu meilleures du côté de Chancy,
              tout à l’ouest du canton, ou au bord du lac à Hermance.
            </p>
            <p>
              Un peu plus loin à l’Est, dans le triangle entre Fribourg, Berne
              et Thoune, le Parc naturel du Gantrisch offre de très bonnes
              conditions d’observation. Il compte d’ailleurs quatre plateformes
              d’observation des étoiles.
            </p>
          </Text>
        </TextWidth>
      </div>

      <div className='ui vertical grey-special segment' id='methode'>
        <TextWidth
          style={{ position: 'relative' }}
        >
          <div className='innerWrapperMethodik'>
            <h4>Méthodologie</h4>

            <p>
              Les données sur l'obscurité nocturne nous ont été fournies par{' '}
              <Link href='https://www.wsl.ch/en/index.html'>
                L'Institut fédéral
              </Link>{' '}
              de recherches sur la forêt, la neige et le paysage. Les émissions
              lumineuses indiquent la quantité de lumière émise par la Terre
              dans l'espace durant la nuit. Celles-ci sont basées sur des
              observations d'images satellites. La luminosité de la nuit est
              calculée en moyenne pour une année civile. Les données sont
              complétées en atténuant les pics annuels et les imprécisions du
              géoréférencement par satellite.
              <br />
              Vous trouverez davantage d'informations sur l'acquisition et le
              traitement des données ainsi que l'impact des émissions lumineuses
              sur les forêts suisses dans{' '}
              <Link href='https://interactif.24heures.ch/2021/perseides/Kienast-2019-Wie_Lichtemissionen_den_Schweizer_Wald-published_version.pdf'>
                cet article
              </Link>
              .
            </p>
            <p>
              Le modèle numérique du terrain, utilisé pour calculer la
              visibilité du ciel et l'altitude, provient de l'Office fédéral de
              la topographie{' '}
              <Link href='https://www.swisstopo.admin.ch/fr/geodata/height/dhm25200.html'>
                swisstopo
              </Link>
              . Celui-ci ne prend en compte que la surface du pays, sans la
              végétation et les constructions.
            </p>
            <p>
              La visibilité du ciel a été calculée grâce au module SAGA{' '}
              <Link href='http://www.saga-gis.org/saga_tool_doc/2.2.0/ta_lighting_3.html'>
                Sky View Factor
              </Link>
              . Le relief environnant sert de base de calcul. Si vous vous
              trouvez à flanc de montagne, par exemple, la visibilité du ciel
              sera faible. Au contraire, dans une plaine, votre champ de vision
              s'élargit. Le résultat a ensuite été affiné en éliminant les{' '}
              <Link href='https://www.swisstopo.admin.ch/fr/geodata/landscape/tlm3d.html'>
                zones
              </Link>{' '}
              couvertes par des forêts.
            </p>
            <p>
              <b>Paramètres des frileux</b>
            </p>
            <ul className='map-parameters'>
              <li>Radiance: {'<200'}</li>
              <li>Ciel visible: {'>75%'}</li>
              <li>Altitude: {'<500m'}</li>
            </ul>
            <p>
              <b>Paramètres des puristes</b>
            </p>

            <ul className='map-parameters'>
              <li>Radiance: {'<50'}</li>
              <li>Ciel visible: {'>90%'}</li>
            </ul>
            <p>
              <b>Paramètres «en train»</b>
            </p>

            <ul className='map-parameters'>
              <li>Radiance: {'<100'}</li>
              <li>Ciel visible: {'>85%'}</li>
              <li>Altitude: {'<2000m'}</li>
            </ul>
          </div>
        </TextWidth>
      </div>

    </AppContainer>
  )
}
const AppContainer = styled(TamediaStyles)`${GlobalVariables}
  font-family: var(--font-sans-serif);
  font-size: 1.8rem;
  line-height: 3rem;
  margin-bottom: 60px;
`

const MapContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
`

const Text = styled.div`
  font-family: var(--font-serif);
`

export default MainComponent
